import React, { ChangeEvent, FC, Fragment, useEffect, useRef, useState } from 'react';
import { BaseModal, Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as UploadIcon } from 'assets/icons/upload-csv-icon.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash-icon.svg';
import { ReactComponent as FileIcon } from 'assets/icons/file.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as DownloadTemplateIcon } from 'assets/icons/download-template.svg';
import * as XLSX from 'xlsx';
import useTeamListContext from 'contexts/team-list.context';
import { toSnakeCase } from 'utils/functions';
import { toast } from 'react-toastify';
import { apiService } from 'utils/api';

interface UploadTeamModalType {
  show: boolean;
  onClose: (success?: boolean) => void;
}

interface UploadTeamFooterType {
  onConfirm: () => void;
  disabled: boolean;
  loading: boolean;
}

const UploadTeamFooter: FC<UploadTeamFooterType> = ({ onConfirm, disabled, loading }) => (
  <Button
    small
    color='primary'
    variant='block'
    className='mx-auto'
    loading={loading}
    onClick={onConfirm}
    disabled={disabled}>
    Confirm
  </Button>
);

const UploadTeamListModal: FC<UploadTeamModalType> = ({ show, onClose }) => {
  const downloadLink =
    'https://docs.google.com/spreadsheets/d/1YQt_co6PzbCxmor6AY37OCdVO-ypBnC6-GDMTXCM7UU/edit?usp=sharing';
  const { addTeamFormData, setAddTeamFormData, departments } = useTeamListContext();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState({
    isError: false,
    msg: ''
  });
  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) setFile(files[0]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileDrop = (e: any) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files) setFile(files[0]);
  };

  const getDepartmentId = (name: string) => {
    const departmentObject = departments.find((d) => d.text.toLowerCase() === name.toLowerCase());
    if (departmentObject) return departmentObject.value;
    return '';
  };

  const readFile = () => {
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const fileDetails = workbook.SheetNames[0];
        const teamListDetails: any[] = XLSX.utils.sheet_to_json(workbook.Sheets[fileDetails]);
        const formattedTeamListDetails = teamListDetails.map((row) => {
          const newRow: any = {};
          Object.keys(row).forEach((key) => {
            const snakeCaseKey = toSnakeCase(key);
            newRow[snakeCaseKey] = row[key];
          });
          return newRow;
        });

        if (isValidFileContent(formattedTeamListDetails))
          setAddTeamFormData(
            formattedTeamListDetails.map((row) => ({
              first_name: row.first_name,
              last_name: row.last_name,
              staff_number: row.unique_staff_number,
              department_id: getDepartmentId(row.department.trim()),
              email: row.email || undefined,
              phone_number: row.phone_number ? row.phone_number.replaceAll('-', '') : undefined,
              role: row.role
            }))
          );
      };
    }
  };

  const isValidFileContent = (data: any) => {
    if (!Array.isArray(data)) {
      setError({
        isError: true,
        msg: 'Incorrect xls format, kindly update'
      });
      return false;
    }

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      if (
        !row ||
        !row.first_name ||
        !row.last_name ||
        !row.unique_staff_number ||
        !row.role ||
        !row.department
      ) {
        setError({
          isError: true,
          msg: 'Incorrect xls format, kindly update'
        });
        return false;
      }
    }
    return true;
  };

  const handleDownloadTemplate = () => {
    window.open(downloadLink, '_blank');
  };

  const addBulkTeam = async () => {
    setLoading(true);
    try {
      const res = await apiService.post('kitchen/staff/csv', {
        data: {
          staffs: addTeamFormData
        },
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });
      if (res.status === 201) {
        onClose(true);
        toast(<ToastMessage type='success' message={res.data.message} />);
      }
    } catch (error: any) {
      onClose();
      toast(<ToastMessage type='error' message={error.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setError({
      isError: false,
      msg: ''
    });
    readFile();
  }, [file]);

  return (
    <BaseModal
      isOpen={show}
      title='Upload XLS'
      onRequestClose={() => onClose()}
      footer={
        <UploadTeamFooter
          loading={loading}
          onConfirm={addBulkTeam}
          disabled={addTeamFormData.length === 0 || error.isError || !file}
        />
      }>
      <div>
        <div
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          onClick={() => fileUploadRef?.current?.click()}
          className={`py-12 rounded-lg px-4 border border-[#A3A3A3] border-dashed ${
            file ? '' : 'bg-[#fafafa]'
          }  cursor-pointer`}>
          <div>
            <Fragment>
              {file ? (
                <div className='flex items-center justify-between py-2 px-3 bg-[#FAFAFA] rounded-md relative bottom-5'>
                  <div className='flex items-center'>
                    <FileIcon className='mr-2' />
                    <span className='text-sm text-[#1A1A1A]'>{file.name}</span>
                  </div>
                  <TrashIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setAddTeamFormData([]);
                      if (fileUploadRef.current) fileUploadRef.current.value = '';
                    }}
                  />
                </div>
              ) : (
                <div className='flex justify-center'>
                  <UploadIcon />
                </div>
              )}

              <p className='mt-2 text-center text-xs'>
                <span className='text-[#0EA3FD] font-medium underline cursor-pointer'>
                  {file ? 'Replace' : 'Upload'} XLS file
                </span>
                <span className='ml-1 text-[#858585]'>or drag and drop</span>
              </p>
            </Fragment>
          </div>
          <input
            ref={fileUploadRef}
            type='file'
            hidden
            accept='.xlsx, .xls'
            onChange={handleFileChange}
          />
        </div>
        {error.isError ? (
          <div className='bg-[#FEF7E1] py-[4px] px-[10px] mt-3 rounded flex items-center'>
            <InfoIcon className='mr-2' />
            <p className='font-medium text-sm text-[#BD8F05]'>{error.msg}</p>
          </div>
        ) : null}

        <Button
          onClick={handleDownloadTemplate}
          small
          variant='link'
          color='transparent'
          className='text-sm mt-4'>
          <DownloadTemplateIcon className='mx-1' />
          Download Template
        </Button>
      </div>
    </BaseModal>
  );
};

export default UploadTeamListModal;
